/**
 * The storefront templates an environment can choose (`storefront_configs.storefront_template`):
 * a page template for every templated page (`STOREFRONT_TEMPLATE_PAGES`), resolved with the
 * store's per-page picks (`storefront_configs.page_templates`, read through `parsePageTemplates`)
 * by `resolveStorefrontPageTemplates`. The storefront and the dashboard's theme preview draw the
 * result through the design system's catalogs, and the dashboard's Pages page offers the ids.
 */
import { z } from "zod";

export const STOREFRONT_TEMPLATE_IDS = ["showroom"] as const;
export type StorefrontTemplateId = (typeof STOREFRONT_TEMPLATE_IDS)[number];

export const storefrontTemplateIdSchema = z.enum(STOREFRONT_TEMPLATE_IDS);

/** What an environment renders until an editor picks another. */
export const DEFAULT_STOREFRONT_TEMPLATE_ID: StorefrontTemplateId = "showroom";

/** Each template's name and one-line description, as the dashboard shows them. */
export const STOREFRONT_TEMPLATE_INFO: Record<
  StorefrontTemplateId,
  { label: string; description: string }
> = {
  showroom: {
    label: "Showroom",
    description:
      "A catalog-first store: Home leads with the store's banners, or a hero on the stage while none is live, then the shopper's best recent opens and the featured boxes; the catalog is a grid under tabs and filters; a box's page sets the box under a spotlight above its details.",
  },
};

/**
 * Every templated storefront page and the page template ids it can be drawn with: the keys of
 * the design system's catalog for that page (`HOME_TEMPLATES`, `BOX_PAGE_TEMPLATES`, …), which a
 * check there keeps equal to these. Ids only: which template a store's page uses is the store's
 * choice, resolved here, and the design system only turns an id into its template.
 */
export const STOREFRONT_PAGE_TEMPLATE_IDS = {
  home: ["showroom", "spotlight", "arcade"],
  boxes: ["showroom", "arcade"],
  battles: ["showroom", "arcade"],
  battleCreate: ["showroom", "arcade"],
  battle: ["showroom", "arcade"],
  account: ["showroom"],
  inventory: ["showroom"],
  box: ["stage", "split"],
  forge: ["showroom", "arcade"],
  forgeBuilder: ["showroom", "arcade"],
  forgeMine: ["showroom", "arcade"],
  login: ["showroom"],
  fairness: ["showroom"],
} as const;

/** A templated storefront page. */
export type StorefrontPage = keyof typeof STOREFRONT_PAGE_TEMPLATE_IDS;

/** Every templated storefront page. */
export const STOREFRONT_PAGES = Object.keys(
  STOREFRONT_PAGE_TEMPLATE_IDS,
) as readonly StorefrontPage[];

export const storefrontPageSchema = z.enum(STOREFRONT_PAGES);

/** A page template id `page` can be drawn with. */
export type PageTemplateIdOf<P extends StorefrontPage> =
  (typeof STOREFRONT_PAGE_TEMPLATE_IDS)[P][number];

/** One page template id for every page: what a store renders. */
export type StorefrontPageTemplateIds = { [P in StorefrontPage]: PageTemplateIdOf<P> };

/** A store's own per-page picks over its storefront template; a page left out uses the base. */
export type PageTemplateChoices = Partial<StorefrontPageTemplateIds>;

/**
 * A store's per-page picks as the resolved storefront config carries them, already read through
 * `parsePageTemplates`: each page with its own ids, so the API's document lists them.
 */
export const pageTemplateChoicesSchema = z.object(
  Object.fromEntries(
    STOREFRONT_PAGES.map((page) => [page, z.enum(STOREFRONT_PAGE_TEMPLATE_IDS[page]).optional()]),
  ),
) as z.ZodType<PageTemplateChoices>;

/** A page a store can pick its own layout for: one that offers more than one. */
export type PickableStorefrontPage = {
  [P in StorefrontPage]: (typeof STOREFRONT_PAGE_TEMPLATE_IDS)[P] extends readonly [string]
    ? never
    : P;
}[StorefrontPage];

/**
 * The name and a line about each layout of a page a store can pick one for, as the dashboard's
 * Pages page shows them; a page joins once it offers a second layout, and fails to compile
 * until it does. Each name is the design system's catalog label, which a test there holds equal.
 */
export const STOREFRONT_PAGE_TEMPLATE_INFO: {
  [P in PickableStorefrontPage]: Record<
    PageTemplateIdOf<P>,
    { label: string; description: string }
  >;
} = {
  home: {
    showroom: {
      label: "Showroom",
      description:
        "Catalog first: the store's banners, or the hero on the stage while none is live, then the shopper's best recent opens, the featured boxes, the promo cards and the FAQ.",
    },
    spotlight: {
      label: "Spotlight",
      description:
        "A few boxes up close: the store's banners, or a centered hero while none is live, the featured boxes large, then the best recent opens, the promo cards and a narrow FAQ. Best for a store that features a handful of boxes.",
    },
    arcade: {
      label: "Arcade",
      description:
        "A busy floor: the store's banners with shining calls to action, the featured boxes and the shopper's best pulls on rails, each pull in its rarity, then the battles, the Forge's pitch on a stage and the FAQ, with no promo cards or closing band. Best for a store with many boxes and battles on.",
    },
  },
  boxes: {
    showroom: {
      label: "Showroom",
      description:
        "A breadcrumb and title over tabs for every box, the new ones and the shopper's favorites, the category chips and a row of filters, then the shopper's top hits and the grid.",
    },
    arcade: {
      label: "Arcade",
      description:
        "The store's boxes and the Forge's as two lists a switch apart, under one bar of filters: the search, All or New, a price range, the sort and the volatility. Dense cards, up to eight across. Phones keep the filters in a sheet.",
    },
  },
  forge: {
    showroom: {
      label: "Showroom",
      description:
        "The Forge's title and pitch with the ways to build and to the shopper's own, sort tabs, then the boxes in a grid a page at a time.",
    },
    arcade: {
      label: "Arcade",
      description:
        "Shoppers' boxes beside the store's, a switch apart, under the same bar of filters, each card with its maker and its opens; Create leads the grid. Phones keep the filters in a sheet.",
    },
  },
  forgeBuilder: {
    showroom: {
      label: "Showroom",
      description:
        "The name, the image and the item picker beside the pull rates and a summary that stays in view, each step under its number; phones stack the steps and dock the price and submit at the foot.",
    },
    arcade: {
      label: "Arcade",
      description:
        "Every step on one screen: the name, the cut and the art over the item picker and the pull rates, each scrolling in its own well, the price and Create at the top. Below the desktop the items open in a window, and phones keep the price and Create in a bar at the foot.",
    },
  },
  forgeMine: {
    showroom: {
      label: "Showroom",
      description:
        "The earnings to claim, then the shopper's boxes as rows with their facts and actions.",
    },
    arcade: {
      label: "Arcade",
      description:
        "The earnings to claim beside their all-time totals, then the shopper's boxes as cards under Active and Archived and the Forge's filters, Create at the end of the tabs. Phones keep the totals and the filters in sheets.",
    },
  },
  battles: {
    showroom: {
      label: "Showroom",
      description:
        "Battles as cards in a grid, grouped into open lobbies, in progress and finished, with chips for every battle or the shopper's own.",
    },
    arcade: {
      label: "Arcade",
      description:
        "Battles as one list of wide rows, open first, under a toolbar: sort by price, filter by mode, create. The shopper's own battles get a page of their own.",
    },
  },
  battle: {
    showroom: {
      label: "Showroom",
      description:
        "One battle in a single column: its head and bar, the seats as cards while it waits, the reels in their own frame while it runs, then who won, every seat's pulls and the fairness inputs.",
    },
    arcade: {
      label: "Arcade",
      description:
        "One battle played out on one stage: its seats in a well divided by lines and versus marks, each seat's reel in the seat, a countdown, a tally and the result on the stage, the players and their pulls just under it. Phones keep Join in a bar at the foot.",
    },
  },
  box: {
    stage: {
      label: "Stage",
      description:
        "The box under a spotlight with its opener and controls, then recent pulls, the best pull beside the odds, and every item. Phones fit the box and its controls to the first screen and open the details in a sheet.",
    },
    split: {
      label: "Split",
      description:
        "The box and its opener in a column that stays in view beside its details: the best pull beside the odds as a bar, recent pulls and every item. Phones as the stage.",
    },
  },
  battleCreate: {
    showroom: {
      label: "Showroom",
      description:
        "The boxes in a panel beside one panel of the settings and the entry, each box with its rounds and the controls to move it.",
    },
    arcade: {
      label: "Arcade",
      description:
        "Create at the top, the boxes as cards dragged into order beside a column of setting cards, and a picker that filters by price and volatility. Phones keep Create in a bar at the foot.",
    },
  },
};

/** Each storefront template as its pages' template ids. */
export const STOREFRONT_TEMPLATE_PAGES: Record<StorefrontTemplateId, StorefrontPageTemplateIds> = {
  showroom: {
    home: "showroom",
    boxes: "showroom",
    battles: "showroom",
    battleCreate: "showroom",
    battle: "showroom",
    account: "showroom",
    inventory: "showroom",
    box: "stage",
    forge: "showroom",
    forgeBuilder: "showroom",
    forgeMine: "showroom",
    login: "showroom",
    fairness: "showroom",
  },
};

function isStorefrontPage(value: string): value is StorefrontPage {
  return Object.hasOwn(STOREFRONT_PAGE_TEMPLATE_IDS, value);
}

/** Whether `page` offers a layout with this id. */
export function isPageTemplateId<P extends StorefrontPage>(
  page: P,
  id: unknown,
): id is PageTemplateIdOf<P> {
  return (STOREFRONT_PAGE_TEMPLATE_IDS[page] as readonly unknown[]).includes(id);
}

/**
 * A store's per-page picks as stored (`storefront_configs.page_templates`) or cached: each pick
 * whose page still offers it. A page or an id no longer offered is dropped rather than refused,
 * so drift leaves that page on the storefront template instead of failing the store, and
 * anything but an object reads as no picks.
 */
export function parsePageTemplates(value: unknown): PageTemplateChoices {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};
  const picks: Partial<Record<StorefrontPage, string>> = {};
  for (const [page, id] of Object.entries(value)) {
    if (isStorefrontPage(page) && isPageTemplateId(page, id)) picks[page] = id;
  }
  return picks as PageTemplateChoices;
}

/**
 * The page template id every page renders with: the storefront template's, with each of the
 * store's per-page picks laid over it. A pick the page doesn't offer (a retired layout, or a
 * value that drifted) is ignored, so the page falls back to the base rather than failing. The
 * one resolution the storefront and the dashboard's theme preview both use.
 */
export function resolveStorefrontPageTemplates(
  template: StorefrontTemplateId,
  choices: Partial<Record<StorefrontPage, string>> = {},
): StorefrontPageTemplateIds {
  const resolved = { ...STOREFRONT_TEMPLATE_PAGES[template] };
  for (const page of STOREFRONT_PAGES) {
    const choice = choices[page];
    if (isPageTemplateId(page, choice)) (resolved as Record<StorefrontPage, string>)[page] = choice;
  }
  return resolved;
}

/**
 * The sections each page fills in the state the theme preview draws: signed in with the data
 * loaded (Home with no banner live, so its hero rather than its banners); the battle room decided,
 * seen by a shopper with no seat; Account on Settings → Profile; the inventory with units picked;
 * the Forge builder ready to create; sign-in signed out; fairness answering an open's Verify link.
 * A section that fills only in another state (Home's banners, a notice, the room's seats and
 * stage, Account's other panels) is not listed; the boxes page's `categories` fills while the
 * store offers two or more categories its listed boxes are in. Each page's parity suite and the
 * preview's test hold their page to this list, so neither can fall a section behind. The
 * dashboard's Pages page also reads Home's as the sections a store shows; a store with a banner
 * live shows its banners in the hero's place.
 */
export const STOREFRONT_FILLED_SECTIONS = {
  home: ["hero", "promoCards", "recentOpens", "featuredBoxes", "battles", "forge", "faq"],
  boxes: ["breadcrumb", "title", "tabs", "categories", "filters", "topHits", "grid"],
  battles: ["heading", "filter", "list"],
  battleCreate: ["heading", "picks", "settings", "entry"],
  battle: ["header", "outcome", "results", "fairness"],
  account: ["header", "tabs", "settingsNav", "profile"],
  inventory: ["header", "tabs", "vault", "selection"],
  box: ["heading", "opener", "bestPull", "odds", "drops"],
  forge: ["heading", "sort", "grid"],
  forgeBuilder: ["heading", "details", "picker", "rates", "summary"],
  forgeMine: ["heading", "earnings", "boxes"],
  login: ["panel"],
  fairness: ["intro", "verify", "seed"],
} as const satisfies Record<StorefrontPage, readonly string[]>;
